/**
 * بررسی دسترسی‌های سطح پلتفرم (مستقل از سازمان فعال).
 */

function normalizeRoleSlug(value) {
  if (value == null || value === '') return '';
  return String(value).toLowerCase().trim().replace(/[\s-]+/g, '_');
}

function truthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function permissionName(entry) {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object' && typeof entry.name === 'string') return entry.name;
  return null;
}

function collectPermissions(user, extraBuckets = []) {
  if (!user || typeof user !== 'object') return [];
  const buckets = [user.permissions, user.effective_permissions, ...extraBuckets];
  const names = [];
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const entry of bucket) {
      const name = permissionName(entry);
      if (name) names.push(name);
    }
  }
  return names;
}

/**
 * @param {{ role?: string, is_super_admin?: boolean } | null | undefined} user
 */
export function isSuperAdmin(user) {
  if (!user || typeof user !== 'object') return false;
  if (
    truthyFlag(user.is_platform_super_admin) ||
    truthyFlag(user.is_super_admin) ||
    truthyFlag(user.isSuperAdmin) ||
    truthyFlag(user.super_admin)
  ) {
    return true;
  }
  const role = normalizeRoleSlug(user.role);
  if (role === 'super_admin' || role === 'superadmin') return true;
  if (normalizeRoleSlug(user.platform_role) === 'super_admin') return true;
  if (Array.isArray(user.roles)) {
    return user.roles.some((r) => {
      const name = typeof r === 'string' ? r : r?.name || r?.slug;
      const slug = normalizeRoleSlug(name);
      return slug === 'super_admin' || slug === 'superadmin';
    });
  }
  return false;
}

/**
 * @param {{ permissions?: unknown[] } | null | undefined} user
 * @param {string} permission
 */
export function userHasPermission(user, permission) {
  if (!user || !permission) return false;
  const perms = collectPermissions(user);
  if (perms.includes('*')) return true;
  return perms.includes(permission);
}

/** مجوزهای شناخته‌شده برای «ورود به‌جای کاربر» در سطح پلتفرم */
export const PLATFORM_LOGIN_AS_PERMISSIONS = [
  'users.impersonate',
  'users.login_as',
  'support.impersonate',
  'support.login_as',
];

/** مدیریت کاربران پلتفرمی — super_admin یا (ادمین پلتفرم + users.manage) */
export function canManageUsers(user) {
  if (isSuperAdmin(user)) return true;
  return isPlatformAdminUser(user) && userHasPermission(user, 'users.manage');
}

/** آیا کاربر مجوز صریح login-as دارد؟ */
export function hasPlatformLoginAsPermission(user) {
  if (!user) return false;
  return PLATFORM_LOGIN_AS_PERMISSIONS.some((p) => userHasPermission(user, p));
}

/** ورود به‌عنوان کاربر دیگر — سوپرادمین یا ادمین با مجوز login-as */
export function canPlatformLoginAs(user) {
  return isSuperAdmin(user) || hasPlatformLoginAsPermission(user);
}

/** ادمین پلتفرم (نه سوپرادمین) — نقش admin یا مجوز users.manage */
export function isPlatformAdminUser(user) {
  if (!user || typeof user !== 'object' || isSuperAdmin(user)) return false;
  if (truthyFlag(user.is_platform_admin)) return true;
  const role = normalizeRoleSlug(user.role);
  if (role === 'admin') return true;
  if (normalizeRoleSlug(user.platform_role) === 'admin') return true;
  if (Array.isArray(user.platform_roles)) {
    return user.platform_roles.some((r) => {
      const name = typeof r === 'string' ? r : r?.name || r?.slug;
      return normalizeRoleSlug(name) === 'admin';
    });
  }
  if (Array.isArray(user.roles)) {
    return user.roles.some((r) => {
      const name = typeof r === 'string' ? r : r?.name || r?.slug;
      const slug = normalizeRoleSlug(name);
      return slug === 'admin' || slug === 'super_admin';
    });
  }
  return false;
}

/**
 * آیا actor می‌تواند به‌جای target وارد شود؟
 * سوپرادمین: همه به‌جز سوپرادمین‌های دیگر.
 * ادمین با مجوز login-as: فقط کاربران عادی.
 */
export function canImpersonateTarget(actor, target) {
  if (!actor || !target || !canPlatformLoginAs(actor)) return false;
  if (String(target.id) === String(actor.id)) return false;
  if (isSuperAdmin(target) || truthyFlag(target.is_platform_super_admin)) return false;
  if (isSuperAdmin(actor)) return true;
  if (isPlatformAdminUser(target) || truthyFlag(target.is_platform_admin)) return false;
  return true;
}

/** تنظیمات نقش ادمین پلتفرم — فقط سوپرادمین */
export function canManagePlatformRoles(user) {
  return isPlatformSuperAdmin(user);
}

/** ویرایش مجوزهای اختصاصی یک ادمین پلتفرم — فقط سوپرادمین */
export function canEditUserPlatformPermissions(user) {
  return isPlatformSuperAdmin(user);
}

/** سوپرادمین پلتفرم — بدون نیاز به سازمان فعال */
export function isPlatformSuperAdmin(user) {
  if (!user || typeof user !== 'object') return false;
  if (truthyFlag(user.is_platform_super_admin)) return true;
  if (user.requires_active_organization === false) return true;
  return isSuperAdmin(user);
}

/**
 * مشاهدهٔ متن رونوشت داخل جلسه (صورت‌جلسه / ضبط‌ها).
 * سوپرادمین پلتفرم، یا کاربر سازمان با مجوز مشاهدهٔ رونوشت.
 * فهرست سراسری /transcripts همچنان فقط برای سوپرادمین است.
 */
export function canViewTranscriptUi(user) {
  if (!user || typeof user !== 'object') return false;
  if (isPlatformSuperAdmin(user)) return true;
  const names = collectPermissions(user, [user.organization_permissions]);
  if (names.includes('*')) return true;
  return names.includes('transcripts.view') || names.includes('transcripts.view-any');
}

/**
 * نام سرویس‌دهنده هوش مصنوعی (AvalAI / GapGPT / OpenAI / WhisperX)
 * فقط برای سوپرادمین و ادمین پلتفرم — نه سازمان و کاربران عادی.
 */
export function canViewAiVendorNames(user) {
  return isPlatformSuperAdmin(user) || isPlatformAdminUser(user);
}

/** آیا کاربر باید سازمان فعال داشته باشد؟ */
export function requiresActiveOrganization(user) {
  if (!user) return true;
  if (isPlatformSuperAdmin(user)) return false;
  if (user.requires_active_organization === false) return false;
  return true;
}

/** آیا کاربر tenant می‌تواند فضای کاری شخصی بسازد؟ */
export function canCreatePersonalOrganization(user) {
  if (!user) return false;
  return (
    requiresActiveOrganization(user) &&
    !isPlatformSuperAdmin(user) &&
    !isPlatformAdminUser(user)
  );
}

/** فلگ‌های سوپرادمین را صریح و پایدار در state/localStorage نگه می‌دارد */
export function normalizePlatformAdminFlags(user) {
  if (!user || !isPlatformSuperAdmin(user)) return user;
  return {
    ...user,
    is_super_admin: true,
    is_platform_super_admin: true,
    requires_active_organization: false,
  };
}

/**
 * پس از stop impersonation، فلگ‌های سوپرادمین را از snapshot قبل از impersonation بازمی‌گرداند.
 * (چون /auth/me ممکن است is_platform_super_admin را برنگرداند.)
 */
export function restorePlatformAdminProfile(mapped, adminSnapshot) {
  if (!mapped || !adminSnapshot || typeof adminSnapshot !== 'object') return mapped;
  if (!isSuperAdmin(adminSnapshot)) return mapped;
  return normalizePlatformAdminFlags({
    ...mapped,
    is_super_admin: true,
    is_platform_super_admin: true,
    requires_active_organization:
      adminSnapshot.requires_active_organization === false
        ? false
        : mapped.requires_active_organization,
    permissions:
      Array.isArray(adminSnapshot.permissions) && adminSnapshot.permissions.length > 0
        ? adminSnapshot.permissions
        : mapped.permissions,
  });
}

/**
 * پس از bootstrap (/auth/me) — اگر API فلگ سوپرادمین را حذف کرد ولی کش login دارد، بازگردانی کن.
 */
export function reconcilePlatformAdminProfile(fresh, cached) {
  if (!fresh) return fresh;
  const normalized = normalizePlatformAdminFlags(fresh);
  if (isPlatformSuperAdmin(normalized)) return normalized;
  if (cached && isPlatformSuperAdmin(cached)) {
    return restorePlatformAdminProfile(fresh, cached);
  }
  return fresh;
}
